import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { EnvelopeSimple as EnvelopeIcon } from '@phosphor-icons/react/dist/ssr';
import { Phone as PhoneIcon } from '@phosphor-icons/react/dist/ssr/Phone';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';

import type { User } from '@/types/v2/user';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';
import { toast } from 'sonner';

import { SectionLabel } from '@/pages/v2/requests/request/components/request-details-card';
import { CreateOrEditResidentModal } from '../../components/create-or-edit-resident-modal';

function DetailField({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        color="text.secondary"
        sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        {icon && <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }} variant="body2">
          {children}
        </Typography>
      </Stack>
    </Box>
  );
}

interface ResidentDetailsProps {
  resident: User | null;
  isLoading: boolean;
  error?: Error | null;
}

export function ResidentDetails({ resident, isLoading }: ResidentDetailsProps): React.JSX.Element {
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const { community } = useUser();
  const invitationSent = Boolean((resident as any)?.community_invitation_sent);

  const resendInvitationMutation = useMutation({
    mutationFn: async (payload: { email: string; communityId: string | number }) => {
      const response = await axios.post('/api/v1/invitations/resend-community', {
        email: payload.email,
        community_id: String(payload.communityId),
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || (invitationSent ? 'Invitation resent successfully' : 'Invitation sent successfully'));
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to send invitation';
      toast.error(message);
    },
  });

  const handleEditSuccess = React.useCallback(
    () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    [queryClient]
  );

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} width="20%" variant="text" />
        <Stack direction="row" spacing={4}>
          <Skeleton height={40} width="30%" variant="rectangular" sx={{ borderRadius: 1 }} />
          <Skeleton height={40} width="30%" variant="rectangular" sx={{ borderRadius: 1 }} />
          <Skeleton height={40} width="30%" variant="rectangular" sx={{ borderRadius: 1 }} />
        </Stack>
      </Stack>
    );
  }

  if (!resident) {
    return null;
  }

  const fullName = `${resident.first_name ?? ''} ${resident.last_name ?? ''}`.trim();

  return (
    <Box>
      <SectionLabel
        icon={<UserIcon size={14} />}
        action={
          <Tooltip title="Edit User">
            <IconButton onClick={dialog.handleOpen} size="small">
              <PencilSimpleIcon size={16} />
            </IconButton>
          </Tooltip>
        }
      >
        Contact Information
      </SectionLabel>

      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={4}>
          <DetailField label="Full Name" icon={<UserIcon size={13} />}>
            {fullName || '—'}
          </DetailField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DetailField label="Email" icon={<EnvelopeIcon size={13} />}>
            {resident.email ?? '—'}
          </DetailField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DetailField label="Phone" icon={<PhoneIcon size={13} />}>
            {resident.phone || '—'}
          </DetailField>
        </Grid>
      </Grid>

      {resident?.email_verified_at == null && (
        <Stack direction="row" sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip color="warning" label="Unverified" size="small" variant="outlined" />
          {invitationSent ? <Chip color="info" label="Invitation already sent" size="small" variant="soft" /> : null}
          <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }} variant="body2">
            {invitationSent ? 'Invitation already sent. Resend it?' : "This user hasn't verified their email and has not been invited yet."}
          </Typography>
          <Button
            disabled={resendInvitationMutation.isPending}
            onClick={() => {
              resendInvitationMutation.mutate({
                email: resident.email || '',
                communityId: community?.uuid ?? community?.id ?? '',
              });
            }}
            size="small"
            startIcon={<EnvelopeIcon size={14} />}
            sx={{ textTransform: 'none', ml: 'auto' }}
            variant="outlined"
          >
            {resendInvitationMutation.isPending ? (invitationSent ? 'Resending…' : 'Sending…') : (invitationSent ? 'Resend Invitation' : 'Send Invitation')}
          </Button>
        </Stack>
      )}

      <CreateOrEditResidentModal
        communityId={community?.id}
        initialData={resident}
        mode="edit"
        onClose={dialog.handleClose}
        onSuccess={handleEditSuccess}
        open={dialog.open}
        userId={resident.id}
      />
    </Box>
  );
}
